<script setup lang="ts">
  import { computed, shallowRef, useId } from 'vue';
  import type { EChartsOption } from 'echarts';
  import { NumberFormatType, useLocale } from '@tundr/i18n';
  import type {
    StackedBarChartProps,
    StackedBarChartTooltipParam,
  } from './StackedBarChart.types';
  import {
    chartClass,
    containerClass,
    emptyStateClass,
  } from './StackedBarChart.css';
  import { useECharts } from '../_shared/composables/useECharts';
  import { visuallyHiddenClass } from '../_shared/styles/visually-hidden.css';
  import {
    resolveCssVar,
    useResolvedCssVars,
    useResolvedCssVarRecord,
  } from '../_shared/composables/useChartColors';
  import {
    chartSegmentColors,
    chartStructuralColors,
  } from '../_shared/utils/chartColorPalette';
  import { CHART_FALLBACK_COLORS } from '../_shared/utils/chartDefaults';

  const props = withDefaults(defineProps<StackedBarChartProps>(), {
    showLegend: true,
    height: '400px',
    width: '100%',
  });

  const { t, n } = useLocale('ds-components');

  const uid = useId();
  const resolvedTestId = computed(
    () => props.testId ?? `ds-stacked-bar-chart-${uid}`,
  );

  const containerRef = shallowRef<HTMLElement | null>(null);
  const chartRef = shallowRef<HTMLElement | null>(null);

  const resolvedSegmentColors = useResolvedCssVars(
    containerRef,
    chartSegmentColors,
  );
  const resolvedStructural = useResolvedCssVarRecord(
    containerRef,
    chartStructuralColors,
  );

  const isEmpty = computed(() => {
    if (props.segments.length === 0) return true;
    return props.segments.every((seg) => seg.values.every((v) => v === 0));
  });

  const formatValue = (value: number) =>
    props.yAxisFormatter
      ? props.yAxisFormatter(value)
      : n(value, NumberFormatType.MAYBE_DECIMAL);

  const option = computed<EChartsOption | null>(() => {
    if (isEmpty.value) return null;
    if (resolvedSegmentColors.value.length === 0) return null;

    const structural = resolvedStructural.value;
    const segColors = resolvedSegmentColors.value;

    const containerEl = containerRef.value;
    const segmentCount = props.segments.length;
    const colCount = props.categories.length;

    // For each column, find the index of the topmost segment that has a
    // non-zero value. Only that segment gets top border-radius.
    const topSegmentByCol = new Array<number>(colCount).fill(-1);
    for (let col = 0; col < colCount; col++) {
      for (let seg = segmentCount - 1; seg >= 0; seg--) {
        if (props.segments[seg].values[col] > 0) {
          topSegmentByCol[col] = seg;
          break;
        }
      }
    }

    const series = props.segments.map((segment, idx) => {
      const fallback = segColors[idx % segColors.length];
      // Consumer-supplied colors may be CSS var refs (e.g. theme tokens).
      // echarts can't resolve `var(--…)` itself, so resolve against the live
      // container styles. Re-evaluation on theme switch happens because this
      // computed depends on `resolvedSegmentColors` / `resolvedStructural`.
      const color = segment.color
        ? containerEl
          ? resolveCssVar(containerEl, segment.color)
          : segment.color
        : fallback;

      const data = segment.values.map((value, col) => ({
        value,
        itemStyle:
          topSegmentByCol[col] === idx
            ? { borderRadius: [8, 8, 0, 0] as [number, number, number, number] }
            : {},
      }));

      return {
        name: segment.name,
        type: 'bar' as const,
        stack: 'total',
        data,
        itemStyle: { color },
        emphasis: {
          focus: 'series' as const,
        },
      };
    });

    const tooltipConfig = props.tooltipFormatter
      ? {
          formatter: (rawParams: unknown) => {
            const params = (
              Array.isArray(rawParams) ? rawParams : [rawParams]
            ) as StackedBarChartTooltipParam[];
            return props.tooltipFormatter!(params);
          },
        }
      : {};

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor:
          structural.tooltipBackground ||
          CHART_FALLBACK_COLORS.tooltipBackground,
        textStyle: {
          color: structural.tooltipText || CHART_FALLBACK_COLORS.tooltipText,
        },
        borderColor: structural.axisLine || CHART_FALLBACK_COLORS.axisLine,
        borderWidth: 1,
        ...tooltipConfig,
      },
      legend: props.showLegend
        ? {
            bottom: 0,
            left: 0,
            icon: 'circle',
            itemWidth: 10,
            itemHeight: 10,
            textStyle: {
              color: structural.legendText || CHART_FALLBACK_COLORS.legendText,
            },
          }
        : undefined,
      grid: {
        left: '3%',
        right: '4%',
        bottom: props.showLegend ? '15%' : '3%',
        top: '3%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: props.categories,
        axisLabel: {
          color: structural.axisLabel || CHART_FALLBACK_COLORS.axisLabel,
        },
        axisLine: {
          lineStyle: {
            color: structural.axisLine || CHART_FALLBACK_COLORS.axisLine,
          },
        },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          color: structural.axisLabel || CHART_FALLBACK_COLORS.axisLabel,
          ...(props.yAxisFormatter ? { formatter: props.yAxisFormatter } : {}),
        },
        splitLine: { show: false },
      },
      series,
    };
  });

  useECharts(chartRef, option);
</script>

<template>
  <div
    ref="containerRef"
    :class="containerClass"
    :style="{ width: props.width, height: props.height }"
    :data-testid="resolvedTestId"
  >
    <div
      v-if="isEmpty"
      :class="emptyStateClass"
      :data-testid="`${resolvedTestId}-empty`"
    >
      <slot name="no-data" />
    </div>
    <template v-else>
      <div
        ref="chartRef"
        :class="chartClass"
        aria-hidden="true"
        :data-testid="`${resolvedTestId}-canvas`"
      />
      <div :class="visuallyHiddenClass">
        <table :data-testid="`${resolvedTestId}-data-table`">
          <caption>
            {{ ariaLabel || t('stacked_bar_chart.default_caption') }}
          </caption>
          <thead>
            <tr>
              <th scope="col">
                {{ t('stacked_bar_chart.category_header') }}
              </th>
              <th
                v-for="(segment, segmentIndex) in segments"
                :key="segmentIndex"
                scope="col"
              >
                {{ segment.name }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(category, col) in categories" :key="col">
              <th scope="row">{{ category }}</th>
              <td
                v-for="(segment, segmentIndex) in segments"
                :key="segmentIndex"
              >
                {{ formatValue(segment.values[col] ?? 0) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>
